/**
 * Audit Logs Page - HYB MASTER SHOP
 * Activity logging system with checkbox category filters and 6-month retention
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, RefreshCw, Shield, Activity, ShieldOff, Lock, Package, Users, Wallet, Database, Mail, ShoppingBag } from 'lucide-react';
import { fetchAuditLogs } from '../api/admin';
import { useAuth } from '../contexts/AuthContext';
import { formatThaiDateTime } from '../utils/dateUtils';

// Category configuration with colors and icons
const CATEGORIES = [
  { id: 'AUTH', label: 'การเข้าสู่ระบบ', color: 'bg-blue-100 text-blue-700', icon: Lock },
  { id: 'ORDER', label: 'คำสั่งซื้อ', color: 'bg-green-100 text-green-700', icon: Package },
  { id: 'USER', label: 'จัดการผู้ใช้', color: 'bg-purple-100 text-purple-700', icon: Users },
  { id: 'FINANCE', label: 'การเงิน', color: 'bg-yellow-100 text-yellow-700', icon: Wallet },
  { id: 'DATA', label: 'ข้อมูล/ตั้งค่า', color: 'bg-gray-100 text-gray-700', icon: Database },
  { id: 'EMS', label: 'สติกเกอร์ EMS', color: 'bg-orange-100 text-orange-700', icon: Mail },
  { id: 'PRODUCT', label: 'จัดการสินค้า', color: 'bg-pink-100 text-pink-700', icon: ShoppingBag },
] as const;

type CategoryId = typeof CATEGORIES[number]['id'];

function getCategoryConfig(category: string) {
  return CATEGORIES.find(c => c.id === category) || { id: 'OTHER', label: category, color: 'bg-gray-100 text-gray-600', icon: Activity };
}

export function AuditLogs() {
  const { hasPermission } = useAuth();
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<CategoryId[]>(
    CATEGORIES.map(c => c.id)
  );
  const [actorFilter, setActorFilter] = useState('all');

  const { data: logs = [], isLoading, refetch } = useQuery({
    queryKey: ['audit-logs', selectedCategories],
    queryFn: () => fetchAuditLogs({ limit: 200, categories: selectedCategories }),
  });

  const actorList = useMemo(() => {
    const unique = new Set<string>();
    logs.forEach(log => {
      if (log.actorId) {
        unique.add(log.actorId);
      }
    });
    return Array.from(unique);
  }, [logs]);

  const toggleCategory = (categoryId: CategoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        // Don't allow deselecting all categories
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== categoryId);
      }
      return [...prev, categoryId];
    });
  };

  const selectAllCategories = () => {
    setSelectedCategories(CATEGORIES.map(c => c.id));
  };

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const lowered = search.trim().toLowerCase();
      const searchMatch = !lowered ||
        log.summary?.toLowerCase().includes(lowered) ||
        log.action?.toLowerCase().includes(lowered) ||
        log.entityType?.toLowerCase().includes(lowered) ||
        log.actorId?.toLowerCase().includes(lowered);
      const actorMatch = actorFilter === 'all' || log.actorId === actorFilter;
      return searchMatch && actorMatch;
    });
  }, [logs, search, actorFilter]);

  // Check if user has admin permission
  if (!hasPermission('admin')) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center space-y-4">
          <ShieldOff className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">Require Admin role to access</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Shield className="w-6 h-6 text-yellow-500 dark:text-yellow-600" />
            บันทึกการทำรายการ (Activity Logs)
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            ระบบเก็บประวัติการทำรายการของแอดมิน เก็บย้อนหลัง 6 เดือนอัตโนมัติ
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          รีเฟรช
        </button>
      </div>

      {/* Category Checkboxes */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">หมวดหมู่:</span>
          <button
            onClick={selectAllCategories}
            className="text-sm text-yellow-600 dark:text-yellow-500 hover:text-yellow-700 dark:hover:text-yellow-400 underline"
          >
            เลือกทั้งหมด
          </button>
        </div>
        <div className="flex flex-wrap gap-3">
          {CATEGORIES.map(category => {
            const Icon = category.icon;
            const isSelected = selectedCategories.includes(category.id);
            return (
              <label
                key={category.id}
                className={`
                  inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all
                  ${isSelected
                    ? category.color + ' ring-2 ring-offset-1 ring-yellow-400 dark:ring-orange-500 dark:ring-offset-gray-800'
                    : 'bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }
                `}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleCategory(category.id)}
                  className="sr-only"
                />
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{category.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Search and Actor Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
          <div className="relative flex-1">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="ค้นหาจาก action / actor / รายละเอียด"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
            />
          </div>
          <select
            value={actorFilter}
            onChange={event => setActorFilter(event.target.value)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
          >
            <option value="all">ผู้ใช้งานทั้งหมด</option>
            {actorList.map(actor => (
              <option key={actor} value={actor}>{actor}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Activity className="w-5 h-5 text-yellow-500 dark:text-yellow-600" />
            บันทึกรายการล่าสุด
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">{filteredLogs.length} รายการ</span>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            กำลังโหลดข้อมูล...
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="py-16 text-center text-gray-500 dark:text-gray-400 text-sm">
            ไม่พบบันทึกที่ตรงกับเงื่อนไข
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredLogs.map(log => {
              const categoryConfig = getCategoryConfig(log.actionCategory || 'OTHER');
              const CategoryIcon = categoryConfig.icon;
              return (
                <li key={log.id} className="p-6 space-y-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${categoryConfig.color}`}>
                        <CategoryIcon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100">{log.action}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {formatThaiDateTime(log.createdAt)} • โดย {log.actorId || 'ระบบ'}
                        </p>
                        {log.ipAddress && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">IP: {log.ipAddress}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoryConfig.color}`}>
                        {categoryConfig.label}
                      </span>
                      {log.entityType && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          {log.entityType}
                        </span>
                      )}
                    </div>
                  </div>
                  {log.summary && <p className="text-sm text-gray-700 dark:text-gray-300 ml-11">{log.summary}</p>}
                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                    <pre className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-600 dark:text-gray-400 rounded-lg p-3 overflow-x-auto ml-11">
                      {JSON.stringify(log.metadata, null, 2)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
